import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Car, ArrowLeft, MapPin, Phone, Clock, Star, Award, CheckCircle, Calendar, Wrench } from 'lucide-react';
import axios from 'axios';

const TallerDetailPage = () => {
  const { tallerId } = useParams();
  const navigate = useNavigate();
  const [taller, setTaller] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTallerData();
  }, [tallerId]);

  const fetchTallerData = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const [tallerRes, reviewsRes, testimonialsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/public/talleres`),
        axios.get(`${BACKEND_URL}/api/public/talleres/${tallerId}/reviews`),
        axios.get(`${BACKEND_URL}/api/public/talleres/${tallerId}/testimonials`)
      ]);

      const tallerData = tallerRes.data.find(t => t.id === tallerId);
      setTaller(tallerData);
      setReviews(reviewsRes.data);
      setTestimonials(testimonialsRes.data);
    } catch (error) {
      console.error('Error fetching taller:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-5 h-5 ${star <= rating ? 'text-yellow-500 fill-current' : 'text-slate-300'}`}
          />
        ))}
      </div>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-CR', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) return 'Hoy';
    if (diffInDays === 1) return 'Hace 1 día';
    if (diffInDays < 30) return `Hace ${diffInDays} días`;
    if (diffInDays < 60) return 'Hace 1 mes';
    if (diffInDays < 365) return `Hace ${Math.floor(diffInDays / 30)} meses`;
    return `Hace ${Math.floor(diffInDays / 365)} año${diffInDays >= 730 ? 's' : ''}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!taller) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="p-8 text-center">
          <p className="text-slate-600">Taller no encontrado</p>
          <Button onClick={() => navigate('/search')} className="mt-4">
            Volver a búsqueda
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="glass-header">
        <div className="container mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" onClick={() => navigate('/search')} data-testid="back-btn">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-purple-600 rounded-xl flex items-center justify-center">
                  <Car className="w-6 h-6 text-white" />
                </div>
                <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
                  Flix
                </h1>
              </div>
            </div>
            <Button onClick={() => navigate('/auth')} className="bg-purple-600">
              Solicitar Cotización
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Taller Info */}
          <Card className="p-8 mb-8 border-2 border-slate-200">
            <div className="flex justify-between items-start mb-6">
              <div className="flex-1">
                <h2 className="text-4xl font-bold mb-3" style={{ fontFamily: 'Outfit' }}>
                  {taller.company_name}
                </h2>
                <div className="flex items-center space-x-4 mb-4">
                  <div className="flex items-center space-x-2">
                    {renderStars(Math.round(taller.rating))}
                    <span className="text-2xl font-bold">{taller.rating}</span>
                    <span className="text-slate-600">({taller.total_reviews} reseñas)</span>
                  </div>
                  {taller.tier === 'gold' && (
                    <div className="flex items-center space-x-1 bg-yellow-100 px-3 py-1 rounded-full">
                      <Award className="w-4 h-4 text-yellow-600" />
                      <span className="text-sm font-medium text-yellow-600">Taller Verificado</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <div className="flex items-start space-x-3 mb-3">
                  <MapPin className="w-5 h-5 text-purple-600 mt-1" />
                  <div>
                    <p className="font-medium">Ubicación</p>
                    <p className="text-slate-600">{taller.location?.address}</p>
                    <p className="text-slate-600">{taller.location?.city}, Costa Rica</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 mb-3">
                  <Phone className="w-5 h-5 text-purple-600 mt-1" />
                  <div>
                    <p className="font-medium">Teléfono</p>
                    <p className="text-slate-600">{taller.phone}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Clock className="w-5 h-5 text-purple-600 mt-1" />
                  <div>
                    <p className="font-medium">Horario</p>
                    <p className="text-slate-600">Lunes - Viernes: {taller.business_hours?.lunes_viernes || '8:00 AM - 5:00 PM'}</p>
                    <p className="text-slate-600">Sábado: {taller.business_hours?.sabado || '8:00 AM - 1:00 PM'}</p>
                    <p className="text-slate-600">Domingo: {taller.business_hours?.domingo || 'Cerrado'}</p>
                  </div>
                </div>
              </div>

              <div>
                <p className="font-medium mb-3">Servicios Ofrecidos:</p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {taller.services_offered?.map((service, idx) => (
                    <span key={idx} className="flex items-center space-x-1 px-3 py-2 bg-purple-100 text-purple-700 rounded-lg text-sm">
                      <CheckCircle className="w-4 h-4" />
                      <span>{service}</span>
                    </span>
                  ))}
                </div>

                {taller.certifications && taller.certifications.length > 0 && (
                  <div>
                    <p className="font-medium mb-2">Certificaciones:</p>
                    {taller.certifications.map((cert) => (
                      <div key={`cert-${cert}`} className="flex items-center space-x-2 text-sm text-slate-600 mb-1">
                        <Award className="w-4 h-4 text-purple-600" />
                        <span>{cert}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <Button 
              onClick={() => navigate('/auth')}
              size="lg"
              className="w-full bg-purple-600"
            >
              Solicitar Cotización Ahora
            </Button>
          </Card>

          {/* Reviews/Testimonials Section */}
          <div>
            <h3 className="text-3xl font-bold mb-6" style={{ fontFamily: 'Outfit' }}>
              Reseñas de Clientes
            </h3>

            {/* Combined reviews from surveys/testimonials */}
            {testimonials.length === 0 && reviews.length === 0 ? (
              <Card className="p-8 text-center">
                <p className="text-slate-500">Este taller aún no tiene reseñas</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {/* Show testimonials (from surveys) first */}
                {testimonials.map((testimonial) => (
                  <Card key={`testimonial-${testimonial.id}`} className="p-6 border-2 border-purple-50 hover:shadow-lg transition-shadow">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center text-white font-semibold">
                          {testimonial.client_name?.charAt(0) || 'C'}
                        </div>
                        <div>
                          <p className="font-semibold">{testimonial.client_name || 'Cliente'}</p>
                          {/* Vehicle Info */}
                          {testimonial.vehicle_info && (
                            <div className="flex items-center text-sm text-purple-600 mt-0.5">
                              <Car className="w-3 h-3 mr-1" />
                              <span>
                                {testimonial.vehicle_info.make} {testimonial.vehicle_info.model} {testimonial.vehicle_info.year}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end">
                        {renderStars(testimonial.rating)}
                        {/* Date */}
                        <div className="flex items-center text-xs text-slate-500 mt-1">
                          <Calendar className="w-3 h-3 mr-1" />
                          <span>{formatDate(testimonial.created_at)}</span>
                        </div>
                      </div>
                    </div>
                    {/* Service Type Badge */}
                    {testimonial.service_type && (
                      <Badge className="mb-2 bg-purple-100 text-purple-700 text-xs">
                        <Wrench className="w-3 h-3 mr-1" />
                        {testimonial.service_type}
                      </Badge>
                    )}
                    <p className="text-slate-700 leading-relaxed">{testimonial.comment}</p>
                  </Card>
                ))}

                {/* Show traditional reviews */}
                {reviews.map((review) => (
                  <Card key={`review-${review.id}`} className="p-6 border-2 border-purple-50 hover:shadow-lg transition-shadow">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center text-white font-semibold">
                          {review.reviewer_name?.charAt(0) || 'U'}
                        </div>
                        <div>
                          <p className="font-semibold">{review.reviewer_name || 'Usuario'}</p>
                          {/* Vehicle Info */}
                          {review.vehicle_info && (
                            <div className="flex items-center text-sm text-purple-600 mt-0.5">
                              <Car className="w-3 h-3 mr-1" />
                              <span>
                                {review.vehicle_info.make} {review.vehicle_info.model} {review.vehicle_info.year}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end">
                        {renderStars(review.rating)}
                        {/* Date */}
                        <div className="flex items-center text-xs text-slate-500 mt-1">
                          <Calendar className="w-3 h-3 mr-1" />
                          <span>{formatDate(review.created_at)}</span>
                        </div>
                      </div>
                    </div>
                    {/* Service Type Badge */}
                    {review.service_type && (
                      <Badge className="mb-2 bg-purple-100 text-purple-700 text-xs">
                        <Wrench className="w-3 h-3 mr-1" />
                        {review.service_type}
                      </Badge>
                    )}
                    <p className="text-slate-700 leading-relaxed">{review.comment}</p>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TallerDetailPage;
